import { useRef } from 'react'

interface RangeSliderProps {
  min: number
  max: number
  valueMin: number
  valueMax: number
  onChange: (newMin: number, newMax: number) => void
}

/**
 * Dual-handle range slider. Drag the left handle to set the minimum value and
 * the right handle to set the maximum. Handles cannot cross each other.
 */
export default function RangeSlider({ min, max, valueMin, valueMax, onChange }: RangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null)

  // Keep latest values accessible inside pointer-event closures without
  // needing to re-register listeners on every render.
  const valRef = useRef({ min: valueMin, max: valueMax })
  valRef.current = { min: valueMin, max: valueMax }

  const pMin = ((valueMin - min) / (max - min)) * 100
  const pMax = ((valueMax - min) / (max - min)) * 100

  function posToValue(clientX: number): number {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect) return 0
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    return Math.round(ratio * (max - min) + min)
  }

  function startDrag(handle: 'min' | 'max', e: React.PointerEvent) {
    e.preventDefault()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)

    function move(ev: PointerEvent) {
      const v = posToValue(ev.clientX)
      if (handle === 'min') {
        onChange(Math.min(v, valRef.current.max - 1), valRef.current.max)
      } else {
        onChange(valRef.current.min, Math.max(v, valRef.current.min + 1))
      }
    }

    function up(ev: PointerEvent) {
      ;(ev.target as HTMLElement).releasePointerCapture(ev.pointerId)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  return (
    <div
      ref={trackRef}
      className="relative mx-3 h-1.5 rounded-full bg-tg-secondary-bg"
      style={{ touchAction: 'none' }}
    >
      {/* Filled track between the two handles */}
      <div
        className="pointer-events-none absolute h-full rounded-full bg-primary"
        style={{ left: `${pMin}%`, width: `${pMax - pMin}%` }}
      />

      {/* Min handle */}
      <button
        type="button"
        aria-label={`Minimum: ${valueMin}`}
        className="absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full bg-primary shadow-md active:cursor-grabbing active:scale-110"
        style={{ left: `${pMin}%`, touchAction: 'none' }}
        onPointerDown={(e) => startDrag('min', e)}
      />

      {/* Max handle */}
      <button
        type="button"
        aria-label={`Maximum: ${valueMax}`}
        className="absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full bg-primary shadow-md active:cursor-grabbing active:scale-110"
        style={{ left: `${pMax}%`, touchAction: 'none' }}
        onPointerDown={(e) => startDrag('max', e)}
      />
    </div>
  )
}
